import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion, type Variants, type Transition } from 'framer-motion';
import {
  getWeatherAnalyze,
  type WeatherAnalyzeResponse,
  type WeatherFactor,
  type WeatherRecommendation,
  type WeatherGrade,
} from '../api/endpoints';
import { postWeatherAnalyze } from '../api/telemetry';
import { getErrorMessage } from '../utils/errors';
import AIDisclaimerFooter from '../components/AIDisclaimerFooter';
import ModalShell from '../components/ModalShell';
import type { ScreenProps } from '../types/screen';

export interface WeatherInput {
  /** When true, the screen auto-runs the analyze flow on mount (used by tool calls). */
  autoAnalyze?: boolean;
  /** Optional pre-fetched data (skip the analyze step entirely). */
  data?: WeatherAnalyzeResponse;
}

export type WeatherResult = WeatherAnalyzeResponse;

/* ── Motion constants ───────────────────────────────── */
const spring: Transition = { type: 'spring', stiffness: 420, damping: 36, mass: 0.9 };
const quick: Transition = { duration: 0.18 };
const pageVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};
const sectionItem: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: spring },
};
const factorStagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.02 } },
};

const cx = 'w-[18px] h-[18px]';

const IconSun = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);
const IconMap = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3z" />
    <path d="M9 3v15M15 6v15" />
  </svg>
);
const IconShield = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M12 3l7 3v5c0 5-3.5 9-7 10-3.5-1-7-5-7-10V6l7-3Z" />
  </svg>
);
/* ── Factor / recommendation icon registry ──────────────── */
function FactorIcon({ name, size = 18 }: { name: string; size?: number }) {
  const p = {
    viewBox: '0 0 24 24', width: size, height: size, fill: 'none',
    stroke: 'currentColor', strokeWidth: 1.8,
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'sun':
      return (<svg {...p}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>);
    case 'sparkles':
      return (<svg {...p}><path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3Z" /><path d="M19 14l.7 2L22 16.7l-2.3.8L19 20l-.7-2.5L16 16.7l2.3-.7L19 14Z" /></svg>);
    case 'droplet':
      return (<svg {...p}><path d="M12 2s6 6 6 10a6 6 0 1 1-12 0C6 8 12 2 12 2Z" /></svg>);
    case 'thermometer':
      return (<svg {...p}><path d="M14 14.76V5a2 2 0 1 0-4 0v9.76a4 4 0 1 0 4 0Z" /></svg>);
    case 'wind':
      return (<svg {...p}><path d="M3 8h11a3 3 0 1 0-3-3M3 16h15a3 3 0 1 1-3 3M3 12h9" /></svg>);
    case 'shield':
      return (<svg {...p}><path d="M12 3l7 3v5c0 5-3.5 9-7 10-3.5-1-7-5-7-10V6l7-3Z" /></svg>);
    default:
      return (<svg {...p}><circle cx="12" cy="12" r="9" /></svg>);
  }
}

/* ── Grade & level visual mapping (HIGH score = GOOD) ────── */
const GRADE_COLOR: Record<WeatherGrade, string> = {
  excellent: 'var(--c-deep-teal)',
  good: 'var(--c-kivo-teal)',
  fair: '#d9a441',
  poor: 'var(--c-coral)',
  harsh: '#d8694a',
};
const GRADE_TINT: Record<WeatherGrade, string> = {
  excellent: 'var(--c-teal-weak)',
  good: 'var(--c-teal-weak)',
  fair: 'rgba(217,164,65,.16)',
  poor: 'rgba(244,168,142,.22)',
  harsh: 'rgba(216,105,74,.18)',
};

type FactorLevel = WeatherFactor['level'];
const LEVEL_CHIP: Record<FactorLevel, string> = {
  none: 'OK', low: 'Low', moderate: 'Med', high: 'High', severe: 'Severe',
};
function levelTone(level: FactorLevel): 'good' | 'neutral' | 'bad' {
  if (level === 'none' || level === 'low') return 'good';
  if (level === 'moderate') return 'neutral';
  return 'bad';
}
function levelBarColor(level: FactorLevel): string {
  if (level === 'none' || level === 'low') return 'var(--c-kivo-teal)';
  if (level === 'moderate') return '#d9a441';
  return 'var(--c-coral)';
}

const PROFILE_LABELS: Record<string, string> = {
  fair_skin: 'Fair skin',
  deep_skin: 'Deep skin',
  sensitive: 'Sensitive',
  acne_prone: 'Breakout-prone',
  cautious_actives: 'Gentle actives',
};
const profileLabel = (t: string) => PROFILE_LABELS[t] ?? t;

/* Score ring (r=60). Fills proportionally to the 0–100 score. */
const RING_CIRC = 2 * Math.PI * 60;

function ScoreRing({ score, grade }: { score: number; grade: WeatherGrade }) {
  const color = GRADE_COLOR[grade];
  const pct = Math.max(0, Math.min(100, score));
  const dashOffset = RING_CIRC - (RING_CIRC * pct) / 100;
  return (
    <div className="ws-ring">
      <svg width={168} height={168} viewBox="0 0 168 168" aria-hidden style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="84" cy="84" r="60" style={{ stroke: 'var(--c-charcoal-06)' }} strokeWidth="12" fill="none" />
        <motion.circle
          cx="84" cy="84" r="60"
          style={{ stroke: color, strokeDasharray: RING_CIRC }}
          strokeWidth="12" fill="none" strokeLinecap="round"
          initial={{ strokeDashoffset: RING_CIRC }}
          animate={{ strokeDashoffset: dashOffset }}
          transition={{ duration: 1.2, ease: 'easeOut', delay: 0.15 }}
        />
      </svg>
      <div className="ws-ring__inner">
        <div className="ws-ring__num" style={{ color }}>{Math.round(score)}</div>
        <div className="ws-ring__max">/ 100</div>
      </div>
    </div>
  );
}

function FactorRow({ f }: { f: WeatherFactor }) {
  const tone = levelTone(f.level);
  const impact = Math.round(f.weight * 100);
  const stress = Math.max(0, Math.min(100, f.stress));
  return (
    <motion.div className="ws-factor" variants={sectionItem}>
      <div className="ws-factor__head">
        <span className="ws-factor__icon"><FactorIcon name={f.icon} /></span>
        <div className="ws-factor__title">
          <div className="ws-factor__label">{f.label}</div>
          <div className="ws-factor__headline">{f.headline}</div>
        </div>
        <div className="ws-factor__value">{f.display_value}<span className="ws-factor__unit">{f.unit}</span></div>
        <span className={`ws-level ws-level--${tone}`}>{LEVEL_CHIP[f.level]}</span>
      </div>
      <div className="ws-bar">
        <div className="ws-bar__fill" style={{ width: `${stress}%`, background: levelBarColor(f.level) }} />
      </div>
      <div className="ws-factor__meta">
        <p className="ws-factor__detail">{f.detail}</p>
        {impact > 0 && <span className="ws-factor__impact">{impact}% of impact</span>}
      </div>
      {f.personalized_note && (
        <div className="ws-note"><FactorIcon name="sparkles" size={14} /><span>{f.personalized_note}</span></div>
      )}
    </motion.div>
  );
}

function RecCard({ r }: { r: WeatherRecommendation }) {
  return (
    <motion.div className={`ws-rec ws-rec--${r.priority}`} variants={sectionItem}>
      <span className="ws-rec__icon"><FactorIcon name={r.icon} size={16} /></span>
      <div className="ws-rec__body-wrap">
        <p className="ws-rec__title">{r.title}</p>
        <p className="ws-rec__body">{r.body}</p>
      </div>
      <span className="ws-rec__pri">{r.priority}</span>
    </motion.div>
  );
}

function BusyDots() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      Analyzing
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          style={{ display: 'inline-block', width: 4, height: 4, borderRadius: '50%', background: 'currentColor' }}
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2, ease: 'easeInOut' }}
        />
      ))}
    </span>
  );
}

function ResultView({ data }: { data: WeatherAnalyzeResponse }) {
  const w = data.weather;
  const a = data.analysis;
  const aqi = w.air_quality?.['us-epa-index'] ?? 0;

  return (
    <motion.div className="stack-5" variants={pageVariants} initial="hidden" animate="visible">
      {/* Score hero */}
      <motion.div className="weather-card" variants={sectionItem}>
        <div
          className="scan-pattern"
          aria-hidden
          style={{ position: 'absolute', inset: 0, opacity: 0.4, borderRadius: 'inherit', pointerEvents: 'none' }}
        />
        <div className="ws-score">
          <ScoreRing score={a.score} grade={a.grade} />
          <div style={{ minWidth: 0 }}>
            <span className="t-label">Skin weather · {w.location_name || 'your location'}</span>
            <h1 className="ws-headline">{a.headline}</h1>
            <div style={{ marginBottom: 'var(--s-3)' }}>
              <span className="ws-grade" style={{ background: GRADE_TINT[a.grade], color: GRADE_COLOR[a.grade] }}>{a.grade}</span>
            </div>
            <p className="ws-summary">{a.summary}</p>
            {a.personalized && a.profile_factors.length > 0 && (
              <div className="ws-personal">
                <span className="ws-personal__label">Personalized</span>
                {a.profile_factors.map((t) => (
                  <span key={t} className="pill pill--teal">{profileLabel(t)}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Current conditions */}
      <motion.div className="weather-card" variants={sectionItem}>
        <span className="t-label" style={{ display: 'block', marginBottom: 'var(--s-3)' }}>Current conditions</span>
        <div className="ws-conditions">
          <span className="pill">UV {w.uv.toFixed(1)}</span>
          <span className="pill">{w.temp_c.toFixed(0)}°C</span>
          <span className="pill">Humidity {w.humidity}%</span>
          <span className="pill">Wind {w.wind_kph.toFixed(0)} km/h</span>
          <span className="pill">Cloud {w.cloud}%</span>
          {w.air_quality && <span className="pill">US AQI {aqi}</span>}
          <span className="pill">{w.is_day ? 'Daytime' : 'Night'}</span>
        </div>
      </motion.div>

      {/* Factor breakdown */}
      <motion.div className="weather-card" variants={sectionItem}>
        <h3 className="t-h3" style={{ margin: '0 0 var(--s-4)' }}>What&apos;s affecting your <em className="t-italic">skin</em></h3>
        <motion.div className="ws-factors" variants={factorStagger}>
          {a.factors.map((f) => <FactorRow key={f.key} f={f} />)}
        </motion.div>
      </motion.div>

      {/* Recommendations */}
      <motion.div className="weather-card" variants={sectionItem}>
        <h3 className="t-h3" style={{ margin: '0 0 var(--s-4)' }}>Today&apos;s <em className="t-italic">recommendations</em></h3>
        {a.recommendations.length > 0 ? (
          <motion.div className="ws-recs" variants={factorStagger}>
            {a.recommendations.map((r, i) => <RecCard key={i} r={r} />)}
          </motion.div>
        ) : (
          <p className="t-small t-mute" style={{ margin: 0 }}>No special precautions needed right now — keep up your normal routine.</p>
        )}
      </motion.div>
    </motion.div>
  );
}

const WeatherScreen: React.FC<ScreenProps<WeatherInput, WeatherResult>> = ({
  mode,
  input,
  onComplete,
  onCancel,
}) => {
  const nav = useNavigate();
  const prefersReduced = useReducedMotion() ?? false;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<WeatherAnalyzeResponse | null>(input?.data ?? null);

  const explain = useMemo(
    () => [
      { icon: <IconSun />, title: 'Live UV & weather', text: 'We calculate a personal daily score using UV, humidity, wind, temperature, and pollution.' },
      { icon: <IconMap />, title: 'Private & local', text: 'We only use your location to fetch local weather. Coordinates leave your device only for this request.' },
      { icon: <IconShield />, title: 'Actionable tips', text: 'Simple, product-agnostic recommendations based on today\'s conditions.' },
    ],
    [],
  );

  const analyze = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      if (!('geolocation' in navigator)) throw new Error('Geolocation is not supported on this device.');
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 8000,
          maximumAge: 60000,
        });
      });
      const { latitude: lat, longitude: lon } = pos.coords;
      const result = await getWeatherAnalyze(lat, lon, true);
      void postWeatherAnalyze(lat, lon, result.weather.location_name).catch(function ignoreError() { /* non-fatal */ });

      if (mode === 'modal') {
        setData(result);
        setBusy(false);
        onComplete?.(result);
      } else {
        nav('/weather/result', { state: { data: result } });
      }
    } catch (error: unknown) {
      const geoCode = error && typeof error === 'object' && 'code' in error
        ? (error as GeolocationPositionError).code
        : 0;
      if (geoCode === 1) setErr('Location permission is needed to analyze UV and air quality. Enable it and try again.');
      else if (geoCode === 2) setErr('Location unavailable right now. Move to an open area or check connectivity, then try again.');
      else if (geoCode === 3) setErr('Location request timed out. Please try again.');
      else setErr(getErrorMessage(error, 'Failed to analyze weather.'));
      setBusy(false);
    }
  }, [mode, nav, onComplete]);

  const intro = (
    <motion.div className="stack-5" variants={pageVariants} initial="hidden" animate="visible">
      <motion.div className="weather-card" variants={sectionItem}>
        <div
          className="scan-pattern"
          aria-hidden
          style={{ position: 'absolute', inset: 0, opacity: 0.45, borderRadius: 'inherit', pointerEvents: 'none' }}
        />
        <div style={{ position: 'relative' }}>
          <span className="t-label">UV & Weather</span>
          <h1 className="t-h3" style={{ margin: 'var(--s-1) 0 var(--s-3)' }}>
            Skincare tips based on <em className="t-italic">today&apos;s weather</em>
          </h1>
          <p className="t-small t-mute" style={{ margin: '0 0 var(--s-4)', maxWidth: '52ch' }}>
            We use your location to fetch UV, humidity, and air quality, then compute a daily score out of 100.
          </p>
          <motion.button
            className="btn btn--primary"
            onClick={analyze}
            disabled={busy}
            whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
            whileHover={{ y: prefersReduced ? 0 : -1 }}
          >
            {busy ? <BusyDots /> : 'Analyze my weather'}
          </motion.button>
        </div>
      </motion.div>

      <motion.div className="g-3" variants={factorStagger}>
        {explain.map((x, i) => (
          <motion.div key={i} className="card card--sand card--compact card--flat card--bordered" variants={sectionItem}>
            <div className="status-icon status-icon--teal" style={{ marginBottom: 'var(--s-3)' }}>{x.icon}</div>
            <p className="t-small" style={{ margin: 0, fontWeight: 500, color: 'var(--ink)' }}>{x.title}</p>
            <p className="t-small t-mute" style={{ margin: 'var(--s-1) 0 0' }}>{x.text}</p>
          </motion.div>
        ))}
      </motion.div>

      <AnimatePresence>
        {err && (
          <motion.div
            key={err}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={quick}
            className="stack-2"
            style={{ color: 'var(--c-error)', background: 'rgba(196,103,58,0.08)', border: '1px solid rgba(196,103,58,0.2)', borderRadius: 'var(--r-sm)', padding: '10px 14px' }}
          >
            <p className="t-small" style={{ margin: 0, color: 'var(--c-error)' }}>{err}</p>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              On iOS Safari: <strong>Settings → Safari → Location</strong>. On Chrome: tap the location icon in the address bar.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  const body = (
    <AnimatePresence mode="wait" initial={false}>
      {data ? (
        <motion.div
          key="result"
          initial={{ opacity: 0, y: prefersReduced ? 0 : 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={spring}
          className="stack-5"
        >
          <ResultView data={data} />
          {mode === 'page' && (
            <div>
              <button onClick={() => { nav('/weather'); }} className="link t-small">
                Analyze again
              </button>
            </div>
          )}
          {mode === 'page' && <AIDisclaimerFooter />}
        </motion.div>
      ) : (
        <motion.div
          key="intro"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={quick}
        >
          {intro}
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (mode === 'modal') {
    return (
      <ModalShell
        title={data ? 'Weather risk' : 'Analyze weather'}
        subtitle={data ? data.weather.location_name : undefined}
        onClose={() => onCancel?.()}
        widthClass="max-w-3xl"
      >
        <div style={{ padding: 'var(--s-5)' }}>{body}</div>
      </ModalShell>
    );
  }

  return body;
};

export default WeatherScreen;
